import React from "react";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import { Booking, readPatientBookings } from "services/bookings.service";
import { createInvoiceFromBooking } from "services/invoices.service";
import Button from "components/Button/Regular";
import Select from "components/Input/Select";
import vgToast from "utils/toast";
import { ORDER_STATE, getOrderStateLabel } from "constants/bookingEnums";
import {
  Invoice,
  getInvoiceStateLabel,
  readInvoicesForBookings,
} from "services/invoices.service";
import { formatMoney } from "constants/currency";

type Row = {
  id: string;
  reference: string;
  raised: string;
  total: string;
  outstanding: string;
  status: string;
};

/**
 * What this patient has been billed.
 *
 * An invoice is addressed to a contact rather than to an animal, so "this
 * patient's bills" means the invoices whose lines reference one of this patient's
 * bookings. That is narrower than everything the owner owes, and narrower is
 * right on a page about one animal.
 */

function formatDate(value: string | undefined) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

function toRow(invoice: Invoice): Row {
  return {
    id: String(invoice.invoiceId),
    reference: invoice.reference ? invoice.reference.slice(0, 8) : String(invoice.invoiceId),
    raised: formatDate(invoice.createdOn_UTC),
    total: formatMoney(invoice.total, invoice.monetaryCurrency),
    outstanding: formatMoney(invoice.remainingBalance, invoice.monetaryCurrency),
    status: getInvoiceStateLabel(invoice.invoiceState),
  };
}

const Bills = () => {
  const { id } = useParams();
  const { orgId } = useActiveOrg();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const animalId = id ? Number(id) : NaN;
  const canLoad =
    isAuthenticated && orgId > 0 && Number.isFinite(animalId) && animalId > 0;

  const [invoices, setInvoices] = React.useState<Invoice[]>([]);
  const [bookings, setBookings] = React.useState<Booking[]>([]);
  const [raising, setRaising] = React.useState(false);
  const [chosenBooking, setChosenBooking] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!canLoad) {
      setInvoices([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();

      // The patient's bookings first: they are what ties an invoice to this
      // animal, since the invoice itself only knows the contact.
      const bookingRes = await readPatientBookings(token, orgId, animalId);
      if (bookingRes.error) {
        setError(bookingRes.error.message);
        setInvoices([]);
        return;
      }

      setBookings(bookingRes.data ?? []);
      const serviceOrderIds = (bookingRes.data ?? []).map((b) => b.serviceOrderId);
      const { data, error: apiError } = await readInvoicesForBookings(
        token,
        orgId,
        serviceOrderIds
      );
      if (apiError) {
        setError(apiError.message);
        setInvoices([]);
        return;
      }
      setInvoices(data ?? []);
    } catch (e) {
      setError((e as Error).message);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [canLoad, getAccessTokenSilently, orgId, animalId]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Only what has not been billed yet. Offering an invoiced booking would
  // invite billing the same consultation twice.
  const billable = React.useMemo(
    () => bookings.filter((b) => b.orderState !== ORDER_STATE.INVOICED),
    [bookings]
  );

  const raiseInvoice = async () => {
    const booking = billable.find(
      (b) => String(b.serviceOrderId) === chosenBooking
    );
    if (!booking) return;

    setBusy(true);
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await createInvoiceFromBooking(
        token,
        orgId,
        booking.serviceOrderId
      );
      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      vgToast.success("Invoice raised.");
      setRaising(false);
      setChosenBooking("");
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const view = useTableView(React.useMemo(() => invoices.map(toRow), [invoices]));

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-400 m-4">
        <PageSizeSelect value={view.pageSize} onChange={view.setPageSize} />
        <Button
          color="Primary"
          onClick={() => {
            setChosenBooking(
              billable.length === 1 ? String(billable[0].serviceOrderId) : ""
            );
            setRaising(true);
          }}
          disabled={billable.length === 0}
        >
          <span>
            {billable.length === 0 ? "Nothing to invoice" : "Raise an invoice"}
          </span>
        </Button>
      </div>

      <Table<Row>
        columns={{
          reference: "Reference",
          raised: "Raised",
          total: "Total",
          outstanding: "Outstanding",
          status: "Status",
        }}
        rows={view.pageRows}
        sort={view.sort}
        onSortChange={view.toggleSort}
        emptyMessage={
          loading
            ? "Loading bills…"
            : billable.length > 0
              ? "Nothing has been billed for this patient yet."
              : "Nothing has been billed, and there are no bookings waiting to be invoiced."
        }
      />

      {error && <div className="px-4 pb-2 text-sm text-red-500">{error}</div>}

      {raising && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6">
          <button
            type="button"
            aria-label="Close dialog"
            className="absolute inset-0 bg-slate-900/35 backdrop-blur-[2px]"
            onClick={() => {
              if (!busy) setRaising(false);
            }}
          />
          <div className="relative w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-[0_18px_50px_-12px_rgba(15,23,42,0.28)]">
            <div className="border-b border-slate-100 bg-slate-50/70 px-6 py-5">
              <h3 className="text-lg font-semibold text-slate-900">
                Raise an invoice
              </h3>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Bills the client for one of this patient's bookings.
              </p>
            </div>
            <div className="px-6 py-5">
              <Select
                label="Booking"
                placeholder="Choose a booking"
                options={billable.map((b) => ({
                  title: `${
                    b.start ? new Date(b.start).toLocaleString() : "Undated"
                  } - ${getOrderStateLabel(b.orderState)}`,
                  value: String(b.serviceOrderId),
                }))}
                value={chosenBooking}
                onChange={(e) => setChosenBooking(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">
              <Button
                color="Secondary"
                onClick={() => setRaising(false)}
                disabled={busy}
              >
                <span>Cancel</span>
              </Button>
              <Button
                color="Primary"
                onClick={raiseInvoice}
                disabled={busy || !chosenBooking}
              >
                <span>{busy ? "Raising…" : "Raise invoice"}</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      <PaginationBar
        from={view.from}
        to={view.to}
        total={view.total}
        currentPage={view.page}
        totalPages={view.totalPages}
        onPrev={view.goPrev}
        onNext={view.goNext}
        onPageSelect={view.goToPage}
      />
    </>
  );
};

export default Bills;
